import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Award, Users, BookOpen, Check, Star, Loader2, User
} from 'lucide-react';
import { asset } from '../../../shared/utils/asset';
import { SEO } from '../../../shared/components';
import { useSuccessStories } from '../../achievements/hooks/useSuccessStories';

interface CurriculumSuccess {
    title: string;
    description: string;
    achievements: string[];
}

interface Testimonial {
    name: string;
    quote: string;
    image: string; // path to the image asset
}

const curriculumSuccessData: CurriculumSuccess[] = [
    {
        title: "CBSE Curriculum",
        description: "100% pass rate with 70% of students scoring above 85% in the 2024 board examinations.",
        achievements: [
            "Top scorers in Mathematics and Science",
            "3 students in the 90%+ club",
        ],
    },
    {
        title: "IGCSE Curriculum",
        description: "Exceptional results with all students achieving A* to C grades in the 2024 examinations.",
        achievements: [
            "4 students achieved straight A*s",
            "Outstanding performance in Language and Sciences",
        ],
    },
    {
        title: "IB Curriculum",
        description: "Strong performance with an average score of 36 points in the 2024 IB Diploma Programme.",
        achievements: [
            "One student achieved a perfect score of 45",
            "Outstanding Extended Essays and TOK presentations",
        ],
    },
];

const testimonials: Testimonial[] = [
    {
        name: "Ponnu Menu",
        quote: "I've seen remarkable improvement in my daughter's academic skills in just 3 months. The teachers are attentive and truly care about each student's progress.",
        image: asset('Achievements/ponnu.png'),
    },
    {
        name: "Aatha Sharma",
        quote: "The worksheets and teaching methods have been incredibly helpful. My child's confidence has grown tremendously since joining Tuition Point.",
               
                image: asset('Achievements/aatha.png'),
    },
    {
        name: "Kesavan Nagaraj",
        quote: "My kids gained admission to BPS thanks to the excellent coaching they received at Tuition Point. The teachers are knowledgeable and motivating.",
        image: asset('Achievements/kesavan.png'),
    },
];

// --- Reusable Components ---

const AchievementStat: React.FC<{ icon: React.ReactNode, value: string, label: string }> = ({ icon, value, label }) => (
    <div className="text-center">
        {/* UPDATED: Changed bg-accent bg-opacity-10 to bg-cyan-100 for consistency */}
        <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4">
            {icon}
        </div>
        <h3 className="text-4xl font-display font-bold mb-2">{value}</h3>
        <p className="text-muted-foreground">{label}</p>
    </div>
);

const CurriculumCard: React.FC<{ data: CurriculumSuccess, delay: number }> = ({ data, delay }) => (
    <div className={`bg-white rounded-xl shadow-lg text-foreground animate-slide-up animation-delay-${delay} p-6`}>
        {/* UPDATED: Changed bg-accent bg-opacity-10 to bg-cyan-100 for consistency */}
        <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center mb-6">
            <BookOpen className="w-6 h-6 text-accent" />
        </div>
        <h3 className="text-xl font-display font-bold mb-3">{data.title}</h3>
        <p className="text-muted-foreground mb-4 text-sm">
            {data.description}
        </p>
        <ul className="space-y-2">
            {data.achievements.map((item, index) => (
                <li key={index} className="flex items-center text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    </div>
);

const StarRating: React.FC = () => (
    <div className="flex text-accent mt-1">
        {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-current" />
        ))}
    </div>
);

const TuitionPointAchievements: React.FC = () => {
    const { stories: successStories, loading: storiesLoading } = useSuccessStories(false);

    return (
        <div className="bg-accent text-white min-h-screen">
            <SEO
                title="Student Achievements & Results - 98% Pass Rate | Tuition Point Qatar"
                description="Proven results at Tuition Point Qatar: 98% pass rate, 93%+ CBSE scores, successful BPS & DPS admissions. See our students' achievements in board exams and entrance tests across Al Wakra."
                keywords="student results Qatar, CBSE results Al Wakra, board exam success Qatar, BPS admission Qatar, DPS entrance Qatar, tuition center achievements, 98% pass rate, IGCSE results Qatar, IB scores Qatar, student testimonials"
            />

            {/* --------------------- Page Header --------------------- */}
            <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">Student Achievements & Results - Al Wakra, Qatar</h1>
                        <p className="text-lg text-white animate-slide-up">100% pass rate, 93%+ CBSE scores, successful BPS & DPS admissions. Proven results in board exams and entrance tests.</p>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Achievement Stats --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-5xl mx-auto">
                        <div className="bg-white rounded-xl shadow-lg text-foreground animate-fade-in shadow-xl p-8 md:p-12">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                                <AchievementStat 
                                    icon={<CheckCircle className="w-8 h-8 text-accent" />}
                                    value="100%"
                                    label="Pass Rate in 2026"
                                />
                                <AchievementStat
                                    icon={<Award className="w-8 h-8 text-accent" />}
                                    value="98%"
                                    label="Highest Score in CBSE"
                                />
                                <AchievementStat 
                                    icon={<Users className="w-8 h-8 text-accent" />}
                                    value="100%"
                                    label="School Entrance Success"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Board Exam Results --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">CBSE Board Exam Success Stories - Qatar</h2>
                        <p className="text-lg text-white/80 animate-slide-up">Al Wakra students consistently achieve excellent CBSE board exam results with scores above 93%.</p>
                    </div>
                    
                    {storiesLoading ? (
                        <div className="flex items-center justify-center h-32">
                            <Loader2 className="w-8 h-8 animate-spin text-white" />
                            <span className="ml-2 text-white">Loading success stories...</span>
                        </div>
                    ) : successStories.length === 0 ? (
                        <div className="text-center py-8">
                            <Award className="mx-auto h-12 w-12 text-white/50 mb-4" />
                            <p className="text-white/80">Success stories coming soon!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {successStories.map((story, index) => (
                                <div key={story.id} className={`bg-white rounded-xl shadow-lg text-foreground animate-slide-up animation-delay-${index + 1} p-6 shadow-lg`}>
                                    <div className="flex flex-col md:flex-row gap-6">
                                        <div className="md:w-1/3 flex-shrink-0">
                                            {story.imageUrl ? (
                                                <img
                                                    src={story.imageUrl}
                                                    alt={story.name + " success story"}
                                                    className="w-full h-auto rounded-lg object-cover"
                                                />
                                            ) : (
                                                <div className="w-full aspect-square bg-amber-100 rounded-lg flex items-center justify-center">
                                                    <User className="w-16 h-16 text-amber-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="md:w-2/3">
                                            <div className="flex items-center mb-3">
                                                <span className="tag bg-accent text-white py-1 px-3 rounded-full text-xs font-semibold">{story.board}</span>
                                                <span className="ml-3 text-xs font-medium text-muted-foreground">{story.year}</span>
                                            </div>
                                            <h3 className="text-xl font-display font-bold mb-2">{story.name}'s Success Story</h3>
                                            <p className="text-accent font-bold text-lg mb-2">{story.score} in {story.board}</p>
                                            {story.quote && (
                                                <p className="text-muted-foreground mb-4 text-sm italic">
                                                    "{story.quote}"
                                                </p>
                                            )}
                                            {story.currentSchool && (
                                                <p className="text-sm text-primary font-medium">
                                                    {story.currentSchool}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
            
            {/* --------------------- Curriculum Success --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">CBSE, IGCSE & IB Results in Al Wakra</h2>
                        <p className="text-lg text-white/80 animate-slide-up">Tuition Point Qatar students excel in CBSE, IGCSE, and IB board examinations with proven results.</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {curriculumSuccessData.map((data, index) => (
                            <CurriculumCard key={index} data={data} delay={index + 1} />
                        ))}
                    </div>
                </div>
            </section>
            
            {/* --------------------- School Entrance Achievements --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 animate-slide-up">BPS & DPS School Admission Success in Qatar</h2>
                        <p className="text-lg text-white text-opacity-90 animate-slide-up">
                            Our dedicated entrance exam preparation program in Al Wakra has helped 20+ students gain admission to Birla Public School and DPS Modern Indian School.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Birla Public School */}
                        <div className="bg-accent bg-opacity-10 rounded-xl p-6 animate-slide-up">
                            <div className="flex items-center mb-4">
                                <div className="w-12 h-12 rounded-full bg-blue-50 text-accent flex items-center justify-center mr-4">
                                    <CheckCircle className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-bold">Birla Public School</h3>
                            </div>
                            <p className="text-white text-opacity-80 mb-4 text-sm">
                                8 students successfully gained admission to Birla Public School through our entrance exam preparation program.
                            </p>
                            <p className="italic text-white text-opacity-70 text-xs">
                                "The teachers at Tuition Point provided us with excellent practice materials and mock tests that were very similar to the actual entrance exam." - Parent of a successful candidate
                            </p>
                        </div>
                        
                        {/* DPS Modern Indian School */}
                        <div className="bg-accent bg-opacity-10 rounded-xl p-6 animate-slide-up animation-delay-1">
                            <div className="flex items-center mb-4">
                                <div className="w-12 h-12 rounded-full bg-blue-50 text-accent flex items-center justify-center mr-4">
                                    <CheckCircle className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-bold">DPS Modern Indian School</h3>
                            </div>
                            <p className="text-white text-opacity-80 mb-4 text-sm">
                                12 students successfully secured admission to DPS Modern Indian School after completing our entrance exam preparation program.
                            </p>
                            <p className="italic text-white text-opacity-70 text-xs">
                                "The personalized approach and attention to detail in the entrance exam preparation made all the difference for my child." - Parent of a successful candidate
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Parent Testimonials --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">What Parents Say</h2>
                        <p className="text-lg text-white/80 animate-slide-up">The trust and appreciation of our students' parents reflect our commitment to excellence.</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className={`bg-white rounded-xl shadow-lg text-foreground animate-slide-up animation-delay-${index + 1} p-6 shadow-lg`}>
                                <div className="flex flex-col items-center mb-4">
                                    <div className="w-16 h-16 rounded-full overflow-hidden mb-3">
                                        <img 
                                            src={testimonial.image} 
                                            alt={testimonial.name} 
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="text-center">
                                        <h3 className="font-display font-bold">{testimonial.name}</h3>
                                        <StarRating />
                                    </div>
                                </div>
                                <p className="text-muted-foreground text-center text-sm">
                                    "{testimonial.quote}"
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* --------------------- CTA Section --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6 animate-slide-up">Achieve Your Academic Goals in Al Wakra</h2>
                        <p className="text-lg text-white/80 mb-8 animate-slide-up">Join Tuition Point Qatar and achieve excellence in CBSE, IGCSE, IB board exams and school entrance tests.</p>
                       <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                                     <Link
                                       to="/enrollment"
                                       className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300 sm:w-auto text-lg"
                                     >
                                       Enroll Today
                                     </Link>
                                     <Link
                                       to="/contact" 
                                       className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300 sm:w-auto text-lg"
                                     >
                                       Contact Us
                                     </Link>
                                   </div>
                    </div>
                </div>
            </section>
            
           
        </div>
    );
};

export default TuitionPointAchievements;
